import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import Colors from '../global/Colors';

const CursorContainer = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
`;

interface CursorPaintProps {
    colorMode: "dark" | "light";
}

const CursorPaint = ({ colorMode }: CursorPaintProps) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const context = canvas.getContext('2d', { willReadFrequently: true });
        if (!context) return;

        // Add a function to keep the canvas 100% width and height
        function resizeCanvas() {
            if (canvas) {
                canvas.width = window.innerWidth;
                canvas.height = window.innerHeight;
                context.clearRect(0, 0, canvas.width, canvas.height);
            }
        }

        window.addEventListener('resize', resizeCanvas, false);
        resizeCanvas();

        // Create variables for any data that needs to be tracked
        let x: number | null = null;
        let y: number | null = null;
        let previousX: number | null = null;
        let previousY: number | null = null;
        let width: number | null = null;
        let color = colorMode === 'dark' ? Colors.black : Colors.white;
        
        // Configuration for color cycling
        const config = {
            COLOR_UPDATE_SPEED: 10,
            PAINT_LIFETIME: 300, // Paint lifetime in milliseconds (1 second)
            FADE_DURATION: 50,  // Duration of the fade-out effect
        };
        
        // Data structure to store paint strokes with their age
        interface PaintStroke {
            x1: number;
            y1: number;
            x2: number;
            y2: number;
            width: number;
            color: string;
            timestamp: number;
            type: 'line' | 'circle' | 'spatter';
            radius?: number;
        }
        
        // Array to store all paint strokes
        const paintStrokes: PaintStroke[] = [];
        
        // Helper function to wrap a value between min and max
        function wrap(value: number, min: number, max: number): number {
            return ((value - min) % (max - min)) + min;
        }
        
        // Variable to track color update timing
        let lastTime = performance.now();
        let isMoving = false;
        let lastMoveTime = 0;
        let colorUpdateAnimationId: number | null = null;
        
        // Function to update the color automatically (only when mouse is moving)
        function updateColor() {
            const currentTime = performance.now();
            
            // Only update color if the mouse is moving
            if (isMoving) {
                // Use HSL-based color cycling for smooth rainbow transitions
                const t = currentTime / 1000;
                const hue = ((t * config.COLOR_UPDATE_SPEED * 10) % 360);
                const saturation = 1.0; // Full saturation
                const lightness = 0.5; // Medium lightness
                
                // Convert HSL to RGB
                const c = (1 - Math.abs(2 * lightness - 1)) * saturation;
                const x = c * (1 - Math.abs((hue / 60) % 2 - 1));
                const m = lightness - c / 2;
                
                let r, g, b;
                if (hue < 60) {
                    [r, g, b] = [c, x, 0];
                } else if (hue < 120) {
                    [r, g, b] = [x, c, 0];
                } else if (hue < 180) {
                    [r, g, b] = [0, c, x];
                } else if (hue < 240) {
                    [r, g, b] = [0, x, c];
                } else if (hue < 300) {
                    [r, g, b] = [x, 0, c];
                } else {
                    [r, g, b] = [c, 0, x];
                }
                
                // Convert RGB to hex
                const toHex = (value: number) => {
                    const hex = Math.round((value + m) * 255).toString(16);
                    return hex.length === 1 ? '0' + hex : hex;
                };
                
                color = `#${toHex(r)}${toHex(g)}${toHex(b)}`;
                
                // Check if mouse has stopped moving (no movement for 100ms)
                if (currentTime - lastMoveTime > 100) {
                    isMoving = false;
                }
            }
            
            // Request the next animation frame
            colorUpdateAnimationId = requestAnimationFrame(updateColor);
        }
        
        // Start the color update animation
        colorUpdateAnimationId = requestAnimationFrame(updateColor);
        
        // Function to redraw all paint strokes with appropriate opacity
        function redrawCanvas() {
            const currentTime = performance.now();
            
            // Clear the canvas
            context.clearRect(0, 0, canvas.width, canvas.height);
            
            // Filter out strokes that are too old
            const activeStrokes = paintStrokes.filter(stroke => {
                const age = currentTime - stroke.timestamp;
                return age < (config.PAINT_LIFETIME + config.FADE_DURATION);
            });
            
            // Update the paintStrokes array with only active strokes
            paintStrokes.length = 0;
            paintStrokes.push(...activeStrokes);
            
            // Draw each stroke with appropriate opacity
            activeStrokes.forEach(stroke => {
                const age = currentTime - stroke.timestamp;
                
                // Calculate opacity based on age
                let opacity = 1.0;
                if (age > config.PAINT_LIFETIME) {
                    // Start fading out
                    opacity = 1.0 - ((age - config.PAINT_LIFETIME) / config.FADE_DURATION);
                }
                
                // Set the color with opacity
                const colorWithOpacity = addOpacityToColor(stroke.color, opacity);
                
                if (stroke.type === 'line') {
                    // Draw line
                    context.beginPath();
                    context.strokeStyle = colorWithOpacity;
                    context.lineWidth = stroke.width;
                    context.moveTo(stroke.x1, stroke.y1);
                    context.lineTo(stroke.x2, stroke.y2);
                    context.stroke();
                } else if (stroke.type === 'circle') {
                    // Draw circle
                    context.fillStyle = colorWithOpacity;
                    context.beginPath();
                    context.arc(stroke.x2, stroke.y2, stroke.radius || 0, 0, 2 * Math.PI);
                    context.fill();
                } else if (stroke.type === 'spatter') {
                    // Draw spatter
                    context.fillStyle = colorWithOpacity;
                    context.beginPath();
                    context.arc(stroke.x1, stroke.y1, stroke.radius || 0, 0, 2 * Math.PI);
                    context.fill();
                }
            });
            
            // Request next animation frame
            requestAnimationFrame(redrawCanvas);
        }
        
        // Helper function to add opacity to a hex color
        function addOpacityToColor(hexColor: string, opacity: number): string {
            // Ensure opacity is between 0 and 1
            opacity = Math.max(0, Math.min(1, opacity));
            
            // Convert opacity to hex
            const alpha = Math.round(opacity * 255).toString(16).padStart(2, '0');
            
            // Return color with opacity
            return `${hexColor}${alpha}`;
        }
        
        // Start the redraw animation
        redrawCanvas();

        // Add the drawing code on mouse move
        function handleMouseMove(event: MouseEvent) {
            // Set the moving flag to true and update last move time
            isMoving = true;
            lastMoveTime = performance.now();
            
            // Save the position from last mouse move
            previousX = x;
            previousY = y;

            // Get the new position of the mouse
            x = event.clientX;
            y = event.clientY;

            // If this is the second mouse move
            if (previousX != null && previousY != null) {
                // Calculate the horizontal move distance
                let distanceX = Math.round(x - previousX);
                if (distanceX < 0) distanceX *= -1;

                // Calculate the vertical move distance
                let distanceY = Math.round(y - previousY);
                if (distanceY < 0) distanceY *= -1;

                // Calculate the diagonal distance
                let distance = Math.hypot(distanceX, distanceY);
                if (distance > 50) distance = 50;
                
                // Calculate the width based on the distance moved
                let targetWidth = Math.round((50 - distance) / 4);
                if (targetWidth < 1) targetWidth = 1;

                // Instead of setting the width, we ease it
                if (width == null) width = targetWidth;
                else if (targetWidth < width) width--;
                else if (targetWidth > width) width++;

                // Add line stroke to the array
                paintStrokes.push({
                    x1: previousX,
                    y1: previousY,
                    x2: x,
                    y2: y,
                    width: width,
                    color: color,
                    timestamp: performance.now(),
                    type: 'line'
                });

                // If the mouse is moving slow, draw a random number of random sized circles
                if (distance < 5) {
                    // Determine a random number of circles to draw
                    const circleCount = 1 + Math.floor(Math.random() * 1);
                    
                    for (let i = 0; i < circleCount; i++) {
                        const minSize = 5;
                        const maxSize = 20;
                        const radius = minSize + Math.random() * (maxSize - minSize);
                        
                        // Add random offset to the circle position
                        const offsetRange = 10; // Maximum pixels to offset in any direction
                        const offsetX = (Math.random() * 2 - 1) * offsetRange; // Random value between -offsetRange and +offsetRange
                        const offsetY = (Math.random() * 2 - 1) * offsetRange;
                        
                        paintStrokes.push({
                            x1: x + offsetX,
                            y1: y + offsetY,
                            x2: x + offsetX,
                            y2: y + offsetY,
                            width: width,
                            color: color,
                            timestamp: performance.now(),
                            type: 'circle',
                            radius: radius
                        });
                    }
                }  
                // If the mouse is moving fast, draw a circle the size of the stroke
                else {
                    paintStrokes.push({
                        x1: x,
                        y1: y,
                        x2: x,
                        y2: y,
                        width: width,
                        color: color,
                        timestamp: performance.now(),
                        type: 'circle',
                        radius: width / 2
                    });
                }

                // Add some random spatter
                if (Math.random() > 0.7) {
                    paintStrokes.push({
                        x1: x - 20 + Math.random() * 40,
                        y1: y - 20 + Math.random() * 40,
                        x2: 0,
                        y2: 0,
                        width: width,
                        color: color,
                        timestamp: performance.now(),
                        type: 'spatter',
                        radius: 2 + Math.random() * 4
                    });
                }
            }
        }

        // This function changes the color to a random color when the canvas is clicked
        function handleClick() {
            color = '#' + Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0').toUpperCase();
        }

        // Add event listeners to document instead of canvas
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('click', handleClick);

        // Cleanup function to remove event listeners
        return () => {
            window.removeEventListener('resize', resizeCanvas);
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('click', handleClick);
            
            // Cancel the color update animation
            if (colorUpdateAnimationId !== null) {
                cancelAnimationFrame(colorUpdateAnimationId);
            }
        };
    }, []);

    return (
        <CursorContainer>
            <canvas 
                ref={canvasRef} 
                style={{ 
                    position: 'fixed', 
                    top: '-1%', 
                    left: '-1%', 
                    height: '102%', 
                    width: '102%', 
                    opacity: 0.8,
                    pointerEvents: 'none', // Change to none to allow events to pass through
                    zIndex: 1
                }} 
            />
        </CursorContainer>
    );
};

export default CursorPaint;